import { useEffect, useMemo, useState } from "react";
import { Library, X } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtDateTime, relTime } from "../lib/format";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import ArtifactView from "../components/ArtifactView";

const KIND_LABEL = {
  product_spec: "Product Spec",
  engineering_plan: "Engineering Plan",
  qa_report: "QA Report",
  release_bundle: "Release Bundle",
};
const KIND_TONE = {
  product_spec: "purple",
  engineering_plan: "blue",
  qa_report: "amber",
  release_bundle: "green",
};

const RANGES = [
  { id: "7d", label: "Last 7 days", days: 7 },
  { id: "30d", label: "Last 30 days", days: 30 },
  { id: "all", label: "All time", days: null },
];

export default function ArtifactsPage() {
  const [items, setItems] = useState([]);
  const [missions, setMissions] = useState([]);
  const [agents, setAgents] = useState([]);
  const [kind, setKind] = useState("all");
  const [missionId, setMissionId] = useState("all");
  const [range, setRange] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      const [{ data: art }, { data: ms }, { data: ag }] = await Promise.all([
        api.get("/artifacts?limit=500"),
        api.get("/missions"),
        api.get("/agents"),
      ]);
      setItems(art);
      setMissions(ms);
      setAgents(ag);
    })();
  }, []);

  const agentById = useMemo(() => {
    const m = {};
    for (const a of agents) m[a.id] = a;
    return m;
  }, [agents]);

  const missionById = useMemo(() => {
    const m = {};
    for (const x of missions) m[x.id] = x;
    return m;
  }, [missions]);

  const filtered = useMemo(() => {
    const now = Date.now();
    const rangeDays = RANGES.find((r) => r.id === range)?.days;
    return items.filter((a) => {
      if (kind !== "all" && a.kind !== kind) return false;
      if (missionId !== "all" && a.mission_id !== missionId) return false;
      if (rangeDays !== null && rangeDays !== undefined) {
        const dt = new Date(a.created_at).getTime();
        if (now - dt > rangeDays * 86400_000) return false;
      }
      return true;
    });
  }, [items, kind, missionId, range]);

  const groups = useMemo(() => {
    const g = {};
    for (const a of filtered) {
      (g[a.kind] ??= []).push(a);
    }
    return g;
  }, [filtered]);

  const kindOrder = ["product_spec", "engineering_plan", "qa_report", "release_bundle"];

  return (
    <>
      <TopBar title="Artifact Library" subtitle="Every deliverable produced by CommandLinc agents." />

      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="panel-body" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <small className="hint">Kind</small>
            <select className="btn small" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="art-kind" style={{ marginLeft: 6 }}>
              <option value="all">All kinds</option>
              {kindOrder.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
            </select>
          </div>
          <div>
            <small className="hint">Mission</small>
            <select className="btn small" value={missionId} onChange={(e) => setMissionId(e.target.value)} data-testid="art-mission" style={{ marginLeft: 6, maxWidth: 300 }}>
              <option value="all">All missions</option>
              {missions.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
            </select>
          </div>
          <div>
            <small className="hint">Range</small>
            <select className="btn small" value={range} onChange={(e) => setRange(e.target.value)} data-testid="art-range" style={{ marginLeft: 6 }}>
              {RANGES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
          <span className="pill dim" style={{ marginLeft: "auto" }} data-testid="art-count">{filtered.length} shown</span>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="panel"><div className="panel-body"><EmptyState title="No artifacts match these filters." icon={Library} /></div></div>
      ) : (
        kindOrder.filter((k) => (groups[k] || []).length > 0).map((k) => (
          <div className="panel" key={k} style={{ marginBottom: 14 }} data-testid={`art-group-${k}`}>
            <div className="panel-head">
              <div>
                <h2>
                  <span className={`pill ${KIND_TONE[k]}`} style={{ marginRight: 8 }}>{KIND_LABEL[k]}</span>
                  {groups[k].length} artifact{groups[k].length === 1 ? "" : "s"}
                </h2>
              </div>
            </div>
            <div className="panel-body">
              {groups[k].map((a) => {
                const ag = agentById[a.produced_by_agent_id];
                const mission = missionById[a.mission_id];
                return (
                  <button
                    key={a.id}
                    className="wide-row row-button"
                    onClick={() => setSelected(a)}
                    data-testid={`art-row-${a.id}`}
                    style={{ width: "100%", textAlign: "left" }}
                  >
                    <div>
                      <b>{a.title}</b>
                      <p className="hint">
                        {mission ? (
                          <Link to={`/commandlinc/missions/${mission.id}`} onClick={(e) => e.stopPropagation()}>
                            {mission.title}
                          </Link>
                        ) : "—"}
                        {" · "}stage <span className="mono">{a.stage_key}</span>
                        {ag ? <> · {ag.name} <span className="hint">({ag.provider}/{ag.model})</span></> : null}
                      </p>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div className="mono" style={{ fontSize: 11 }}>{a.content_hash?.slice(0, 12)}…</div>
                      <small className="hint">{relTime(a.created_at)}</small>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}

      {selected ? (
        <div className="drawer-backdrop" onMouseDown={() => setSelected(null)} data-testid="art-drawer">
          <aside className="drawer" onMouseDown={(e) => e.stopPropagation()}>
            <div className="drawer-head">
              <div>
                <span className="eyebrow">{KIND_LABEL[selected.kind] || selected.kind}</span>
                <h2>{selected.title}</h2>
                <p className="hint" style={{ fontSize: 11 }}>
                  <span className="mono">{selected.content_hash?.slice(0, 16)}…</span>
                  {" · "}created {fmtDateTime(selected.created_at)}
                  {missionById[selected.mission_id] ? (
                    <> · <Link to={`/commandlinc/missions/${selected.mission_id}`}>mission</Link></>
                  ) : null}
                </p>
              </div>
              <button className="btn small" onClick={() => setSelected(null)} data-testid="art-drawer-close">
                <X size={14} />
              </button>
            </div>
            <div className="drawer-body">
              <ArtifactView artifact={selected} />
            </div>
          </aside>
        </div>
      ) : null}
    </>
  );
}
